
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";
import { toast } from "@/hooks/use-toast";
import { ReadingForm } from "@/components/readings/ReadingForm";
import { ReadingDetailsLoading } from "@/components/readings/ReadingDetailsLoading";
import { ReadingDetailsError } from "@/components/readings/ReadingDetailsError";
import { ReadingFormValues } from "@/components/readings/types";
import { format, isEqual } from "date-fns";
import { readingService } from "@/components/readings/readingService";

type ReadingWithHouse = Tables<"readings"> & {
  houses: {
    name: string;
  };
};

const ReadingDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [initialDate, setInitialDate] = useState<Date>(new Date());

  const { data: reading, isLoading, error: queryError } = useQuery({
    queryKey: ['reading', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('readings')
        .select(`
          *,
          houses (
            name
          )
        `)
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Lectura no encontrada');
      
      // Set the initial date when reading data is fetched
      if (data.reading_date) {
        setInitialDate(new Date(data.reading_date));
      }
      
      return data as ReadingWithHouse;
    },
  });

  const updateReadingMutation = useMutation({
    mutationFn: async (values: ReadingFormValues) => {
      // Check if status is pending and date hasn't been updated
      if (reading?.status === "pending" && isEqual(initialDate, values.reading_date)) {
        toast({
          title: "Error",
          description: "Por favor agregar la fecha real cuando se tomó esta lectura",
          variant: "destructive",
        });
        return { success: false };
      }

      setIsUploading(true);
      try {
        let photoUrl = reading?.photo_url;

        // Handle file upload if a new file is selected
        if (selectedFile) {
          photoUrl = await readingService.uploadImage(selectedFile);
        }

        const { error } = await supabase
          .from('readings')
          .update({
            type: values.type,
            reading_value: values.reading,
            reading_date: values.reading_date.toISOString(),
            photo_url: photoUrl,
            // Only update status to completed if validation passes
            status: "completed"
          })
          .eq('id', id);

        if (error) throw error;

        return { success: true };
      } finally {
        setIsUploading(false);
      }
    },
    onSuccess: (result) => {
      if (result.success) {
        toast({
          title: "Éxito",
          description: "Lectura actualizada exitosamente",
        });
        queryClient.invalidateQueries({ queryKey: ['reading', id] });
        navigate('/readings');
      }
    },
    onError: (error) => {
      console.error('Error updating reading:', error);
      toast({
        title: "Error",
        description: "Error al actualizar la lectura",
        variant: "destructive",
      });
    }
  });

  useEffect(() => {
    if (reading?.photo_url) {
      setPreviewUrl(reading.photo_url);
    }
  }, [reading]);

  useEffect(() => {
    // Cleanup object URLs only for locally created previews
    return () => {
      if (previewUrl && !previewUrl.includes('supabase.co')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFormSubmit = (data: ReadingFormValues) => {
    // For pending readings, check if the date was changed
    if (reading?.status === "pending" && isEqual(initialDate, data.reading_date)) {
      toast({
        title: "Error",
        description: "Por favor agregar la fecha real cuando se tomó esta lectura",
        variant: "destructive",
      });
      return;
    }
    
    updateReadingMutation.mutate(data);
  };

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    // Create and set preview URL when a file is selected
    if (previewUrl && !previewUrl.includes('supabase.co')) {
      URL.revokeObjectURL(previewUrl);
    }
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
  };

  if (isLoading) {
    return <ReadingDetailsLoading />;
  }

  if (queryError || !reading) {
    return (
      <ReadingDetailsError 
        errorMessage={queryError ? 'Error al cargar la lectura' : 'Lectura no encontrada'} 
      />
    );
  }

  return (
    <div className="p-4 mb-6">
      <Card className="max-w-3xl mx-auto">
        <CardHeader className="pb-4">
          <CardTitle>Detalles de la lectura</CardTitle>
        </CardHeader>
        <CardContent className="pb-4">
          <ReadingForm
            reading={reading}
            onSubmit={handleFormSubmit}
            isSubmitting={updateReadingMutation.isPending}
            selectedFile={selectedFile}
            setSelectedFile={handleFileSelect}
            previewUrl={previewUrl}
            isUploading={isUploading}
            initialDate={initialDate}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default ReadingDetails;
